import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AppShell } from '../../components/AppShell';
import { Button } from '../../components/Button';
import { CustomerCard } from '../../components/CustomerCard';
import { Field, inputStyle } from '../../components/Field';
import { FieldPhotoUploader } from '../../components/FieldPhotoUploader';
import { NotesPanel } from '../../components/NotesPanel';
import { PhotoGrid } from '../../components/PhotoGrid';
import { Spinner } from '../../components/Spinner';
import { StatusChip } from '../../components/StatusChip';
import { apiGet, apiSend, ClientApiError } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import type {
  ReconTeam,
  Referral,
  ReferralEvent,
  ReferralNote,
  ReferralPhoto,
} from '../../../shared/types';

interface Detail {
  referral: Referral;
  notes: ReferralNote[];
  photos: ReferralPhoto[];
  events: ReferralEvent[];
  techName: string | null;
  teamName: string | null;
}

export function JobDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isTech = user?.role === 'tech';
  const [detail, setDetail] = useState<Detail | null>(null);
  const [teams, setTeams] = useState<ReconTeam[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setDetail(await apiGet<Detail>(`/api/referrals/${id}`));
    } catch (caught) {
      setError(caught instanceof ClientApiError ? caught.message : 'Could not load that job.');
    }
  }, [id]);

  useEffect(() => {
    void load();
    if (isTech) {
      apiGet<{ teams: ReconTeam[] }>('/api/teams')
        .then((data) => setTeams(data.teams.filter((t) => t.active)))
        .catch(() => undefined);
    }
  }, [load, isTech]);

  async function act(path: string, body: unknown) {
    setBusy(true);
    setError('');
    try {
      await apiSend(`/api/referrals/${id}/${path}`, 'PATCH', body);
      await load();
    } catch (caught) {
      setError(caught instanceof ClientApiError ? caught.message : 'That did not work. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (error && !detail) {
    return (
      <AppShell title="Job">
        <div className="error-banner">{error}</div>
      </AppShell>
    );
  }
  if (!detail) {
    return (
      <AppShell title="Job">
        <Spinner />
      </AppShell>
    );
  }

  const { referral } = detail;
  const open = referral.status !== 'complete' && referral.status !== 'declined';

  return (
    <AppShell title={referral.refNumber}>
      {error && <div className="error-banner">{error}</div>}

      <div className="row" style={{ justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <strong style={{ color: 'var(--navy)' }}>Status</strong>
        <StatusChip status={referral.status} />
      </div>

      <CustomerCard referral={referral} />

      {detail.teamName && (
        <p className="muted" style={{ marginTop: 0 }}>
          Recon team: {detail.teamName}
        </p>
      )}

      {isTech && open && (
        <div className="card">
          <h2>Recon</h2>
          <Field label="Attach a recon team" htmlFor="team">
            <select
              id="team"
              style={inputStyle}
              disabled={busy}
              value={referral.reconTeamId ?? ''}
              onChange={(e) => e.target.value && void act('recon', { teamId: e.target.value })}
            >
              <option value="">Choose a recon team</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </Field>
          <Button full disabled={busy} onClick={() => void act('status', { status: 'complete' })}>
            Mark complete
          </Button>
        </div>
      )}

      <div className="card">
        <h2>Photos</h2>
        <PhotoGrid photos={detail.photos} />
        {open && (
          <FieldPhotoUploader
            referralId={referral.id}
            onUploaded={(photo) =>
              setDetail((prev) => (prev ? { ...prev, photos: [...prev.photos, photo] } : prev))
            }
            onReload={load}
          />
        )}
      </div>

      <NotesPanel
        referralId={referral.id}
        notes={detail.notes}
        canAdd={open}
        onAdded={(note) =>
          setDetail((prev) => (prev ? { ...prev, notes: [...prev.notes, note] } : prev))
        }
        onReload={load}
      />
    </AppShell>
  );
}
